import { ChangeDetectionStrategy, Component } from '@angular/core'
import { Message, MessageService } from 'primeng/api'

import {
  BUREAU_MOP_LABELS,
  BUREAU_MOP_SHORT_LABELS,
  BUREAU_PYME_SCORE_RANGE,
  decodePortfolioRating,
  formatPesos,
  formatPesosCompact,
  formatShortDate,
  historyTones,
  humanizeCode,
  labelOf,
  lastMonthsRange,
  mopSeverity,
  PillTone,
  PortfolioRatingItem,
  portfolioRatingText,
  scorePosition,
  SEVERITY_TONE,
  worstMop,
} from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { AnnexBureauResult, BureauAccount, BureauReport, BureauSectionData, BureauView, SnapshotResponse, SnapshotSection, SnapshotTab } from '@data/interfaces/verification-snapshot.interface'
import { sectionStateText, SnapshotUiState } from '../snapshot-state/snapshot-state.component'
import { SnapshotTabBase } from '../snapshot-tab.base'
import { MxDatePipe } from '@data/pipes/mx-date.pipe'

/** Una cuenta financiera ya lista para la tabla: el saldo vencido sumado y el peor MOP resuelto. */
export interface BureauAccountRow {
  /** Llave estable de la fila para abrir y cerrar su detalle. */
  key: string
  account: BureauAccount
  overdue: number
  mop: string | null
  mopLabel: string
  /** La forma corta para la pastilla («Atraso 30–59 d»); la larga va en `title` y para el lector. */
  mopShort: string
  mopTone: PillTone
  /** Un cuadrito por mes del historial de pagos, en el orden en que lo manda Buró. */
  history: Array<'ok' | 'warn' | 'bad' | 'none'>
  /** Quita, dación o quebranto con monto: van en rojo porque son pérdida para el otorgante. */
  losses: Array<{ label: string; amount: number }>
}

/** Las vistas del detalle del reporte de Buró en el control segmentado. */
export type BureauSegment = 'financial' | 'commercial' | 'months' | 'alerts'

/** Una fuente de «Fuera del Buró» que no tiene registros que pintar, con su estado. */
export interface SourceWithoutRecords {
  key: string
  label: string
  state: SnapshotUiState
  text: string
}

/** Los seis tramos de antigüedad del Buró, en el orden en que los pinta la franja. */
const AGING_BUCKETS: Array<{ key: keyof BureauAccount; label: string }> = [
  { key: 'saldoVencidoDe1a29Dias', label: '1–29 días' },
  { key: 'saldoVencidoDe30a59Dias', label: '30–59 días' },
  { key: 'saldoVencidoDe60a89Dias', label: '60–89 días' },
  { key: 'saldoVencidoDe90a119Dias', label: '90–119 días' },
  { key: 'saldoVencidoDe120a179Dias', label: '120–179 días' },
  { key: 'saldoVencidoDe180DiasOMas', label: '180+ días' },
]

/** Instituciones financieras que se ven antes de «Ver las N instituciones». */
const TOP_INSTITUTIONS = 5
/**
 * Las barras de moratorios llenan la altura de su tarjeta (mínimo 56 px, maqueta F-158), así que van en % de su caja:
 * la más alta al 100 %, un mes con monto nunca bajo el 10 % y un mes en $0 como raya del 5 %.
 */
const MORATORY_BAR_FLOOR = 10
const MORATORY_BAR_MIN = 5

const LOSSES: Array<{ key: keyof BureauAccount; label: string }> = [
  { key: 'quita', label: 'Quita' },
  { key: 'dacion', label: 'Dación' },
  { key: 'quebranto', label: 'Quebranto' },
]

const INQUIRY_WINDOWS = [3, 12, 24] as const

/**
 * Buró da en miles de pesos la calificación por mes (`historia`) y las cuentas comerciales (`creditoComercial`):
 * el tablero de Syntage pinta en ≈$46 millones el mes que aquí llega como 46,204. Las cuentas financieras
 * (`creditoFinanciero`) ya vienen en pesos y no se tocan (F-158).
 */
const BUREAU_THOUSANDS = 1000

/**
 * Pase directo a número: el reductor ya convierte montos, pero lo que no parsea llega como vino.
 */
function amount(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Sub-pestaña «Buró y deuda» (F-158). Solo se monta con `leases.view`.
 *
 * Deriva en el panel —no en el backend (ADR-0002)— los totales que el reporte no trae hechos: saldo
 * vencido por cuenta y por tramo de antigüedad, y el peor MOP de cada historial.
 *
 * El anexo tardío de Buró y BIL solo LEE en Syntage: no pide reporte nuevo ni consume extracciones. Se ofrece
 * con `leases.update`, nunca sobre una foto anclada (`pinned`) ni en un arrendamiento congelado, donde
 * el backend respondería 409.
 */
@Component({
  selector: 'app-ciec-bureau-tab',
  templateUrl: './ciec-bureau-tab.component.html',
  styleUrls: ['./ciec-bureau-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class CiecBureauTabComponent extends SnapshotTabBase<BureauView> {
  protected readonly tab: SnapshotTab = 'bureau'

  private readonly mxDate = new MxDatePipe()

  /** Búsqueda del anexo en curso: el botón queda en `loading` y un segundo clic no repite la petición. */
  annexing = false
  accountFilter: 'open' | 'closed' = 'open'
  /** Vista abierta en el segmentado del reporte de Buró. */
  segment: BureauSegment = 'financial'
  /** Cuentas con el detalle abierto, por su llave. */
  readonly openRows = new Set<string>()
  /** «Ver las N instituciones»: sin él se ven las 5 de mayor total. */
  showAllInstitutions = false

  // Derivados de la vista (se recalculan en `onView`).
  bureauSection: SnapshotSection<BureauReport> | null = null
  report: BureauReport | null = null
  openAccounts: BureauAccountRow[] = []
  closedAccounts: BureauAccountRow[] = []
  currentBalance = 0
  overdueBalance = 0
  aging: Array<{ label: string; amount: number; share: number }> = []
  /** Barras de intereses moratorios por mes, de la más vieja a la más reciente: mes, monto y alto en % de su caja. */
  moratoryBars: Array<{ month: string; amount: string; height: number; isZero: boolean }> = []
  /** Fuentes de «Fuera del Buró» sin registros: van en la línea «Sin registros:» con su estado. */
  sourcesWithoutRecords: SourceWithoutRecords[] = []
  onTime: { bank: number | null; nonBank: number | null } = { bank: null, nonBank: null }
  inquiries: Array<{ label: string; financial: number | null; commercial: number | null }> = []
  /** Un mes de la calificación de cartera, con sus claves ya agrupadas en una línea y el texto original de Buró. */
  ratingRows: Array<{ month: Record<string, unknown>; rating: string; raw: string | null; items: PortfolioRatingItem[] | null }> = []
  /** Meses que promedia «Promedio 3 meses» de intereses moratorios; `null` si la serie no trae 3. */
  moratoryAverageRange: { from: string; to: string } | null = null
  /** Posición del Score PyME en su escala de 100 a 400, para la barra del hero. */
  scorePercent: number | null = null
  /** Claves de la calificación de cartera del reporte, para la tarjeta del hero. */
  portfolioRating: PortfolioRatingItem[] | null = null

  get sections(): BureauView['sections'] {
    return this.view?.sections ?? {}
  }

  get accounts(): BureauAccountRow[] {
    return this.accountFilter === 'open' ? this.openAccounts : this.closedAccounts
  }

  /** El bloque del PDF (botón o nota) se pinta si la foto registró su archivo: el id del PDF en Syntage no sale por la API. */
  get hasBureauPdf(): boolean {
    return this.fileStatus('bureau') !== null
  }

  /** Anexos visibles en esta revisión: lo anterior se conserva y aquí se dice cuándo entró lo nuevo. */
  get bureauAnnexes(): BureauView['annexes'] {
    return (this.view?.annexes ?? []).filter((annex) => annex.section === 'bureau')
  }

  /** Las vistas del segmentado con su conteo; una sin datos no aparece. */
  get segments(): Array<{ key: BureauSegment; label: string; count: number }> {
    const report = this.report
    if (!report) return []
    return [
      { key: 'financial' as const, label: 'Cuentas financieras', count: this.openAccounts.length + this.closedAccounts.length },
      { key: 'commercial' as const, label: 'Comerciales', count: report.commercialAccounts.length },
      { key: 'months' as const, label: 'Calificación por mes', count: this.ratingRows.length },
      { key: 'alerts' as const, label: 'Alertas', count: report.alerts.length },
    ].filter((segment) => segment.count > 0)
  }

  /** Cuántas de las dos tarjetas de «Fuera del Buró» se pintan: con una sola, ocupa todo el ancho. */
  get outsideCards(): number {
    const moratory = !this.sectionState(this.sections.moratoryInterest) && !!this.sections.moratoryInterest?.data
    const institutions = !this.sectionState(this.sections.financialInstitutions)
    return Number(moratory) + Number(institutions)
  }

  /** Instituciones financieras de mayor a menor total; las 5 primeras hasta pedir todas. */
  get institutions(): BureauSectionData['financialInstitutions']['items'] {
    const items = [...(this.sections.financialInstitutions?.data?.items ?? [])].sort((a, b) => (b.total ?? 0) - (a.total ?? 0))
    return this.showAllInstitutions ? items : items.slice(0, TOP_INSTITUTIONS)
  }

  get institutionsCount(): number {
    return this.sections.financialInstitutions?.data?.items.length ?? 0
  }

  get institutionsTotal(): number {
    return (this.sections.financialInstitutions?.data?.items ?? []).reduce((sum, item) => sum + amount(item.total), 0)
  }

  /** Suma vencida de los tramos con monto, para la línea bajo los seis segmentos. */
  get agingSummary(): string {
    const withAmount = this.aging.filter((bucket) => bucket.amount > 0)
    if (!withAmount.length) return `${formatPesos(0)} en todos los tramos`
    return withAmount.map((bucket) => `${bucket.label} ${formatPesos(bucket.amount)}`).join(' · ')
  }

  setAccountFilter(filter: 'open' | 'closed'): void {
    this.accountFilter = filter
  }

  setSegment(segment: BureauSegment): void {
    this.segment = segment
  }

  toggleRow(key: string): void {
    if (this.openRows.has(key)) this.openRows.delete(key)
    else this.openRows.add(key)
  }

  searchBureau(): void {
    if (!this.context || this.annexing || !this.canAnnex) return
    this.annexing = true
    this.api.annexBureau(this.context).subscribe((response) => {
      this.annexing = false
      this.notifyAnnex(response)
      if (!response.error && response.data?.annexed) this.load(false)
      this.cd.markForCheck()
    })
  }

  /** Un aviso por desenlace del anexo, con el texto que ve el usuario. */
  private notifyAnnex(response: SnapshotResponse<AnnexBureauResult>): void {
    if (!response.error && response.data?.annexed) {
      this.messageService.add(this.annexedToast(response.data))
      return
    }
    if (!response.error && response.data?.reason === 'SAME_REPORT') {
      this.messageService.add({ severity: 'info', summary: 'Ya está al día', detail: 'El reporte de Buró de Syntage es el mismo que ya muestra este análisis.' })
      return
    }
    if (!response.error) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sin reporte de Buró en Syntage',
        detail: 'Syntage todavía no tiene un reporte de Buró para este RFC. Pídelo en el panel de Syntage y vuelve a buscar; la búsqueda no consume extracciones.',
      })
      return
    }
    //! El 409 trae varios motivos que el usuario resuelve distinto; el backend los distingue con `code`.
    //! Solo `LEASE_FROZEN` es el arrendamiento congelado. Lo demás (`PRE_SNAPSHOT`, `NO_VERIFICATION`
    //! o un código nuevo) se avisa con el mensaje de negocio del backend tal cual.
    if (response.status === 409) {
      this.messageService.add(this.conflictToast(response))
      return
    }
    this.messageService.add({ severity: 'error', summary: 'No se pudo consultar Syntage', detail: 'Vuelve a intentarlo en unos minutos; si sigue igual, avísale a Operaciones.' })
  }

  /**
   * El aviso de lo que se agregó. La API responde solo la PRIMERA sección anexada y busca Buró antes que BIL:
   * `bil` quiere decir que el Buró no cambió; `bureau` no dice si BIL también entró, así que ese aviso no lo afirma.
   */
  private annexedToast(result: AnnexBureauResult): Message {
    const date = this.mxDate.transform(result.reportDate)
    if (result.section === 'bil') return { severity: 'success', summary: 'Reporte BIL agregado', detail: `Se agregó el reporte BIL del ${date}; el de Buró no cambió. Lo ves en Legal y corporativo.` }
    return { severity: 'success', summary: 'Reporte de Buró agregado', detail: `Se agregó el reporte del ${date}. Lo anterior se conserva en el historial del análisis.` }
  }

  /** El toast de cada 409 del anexo, por su `code`. */
  private conflictToast(response: SnapshotResponse<AnnexBureauResult>): Message {
    switch (response.code) {
      case 'LEASE_FROZEN':
        return { severity: 'warn', summary: 'El análisis ya no se puede completar', detail: response.msg }
      case 'VERIFICATION_EXPIRED':
        return { severity: 'warn', summary: 'El análisis está vencido', detail: 'Corre un análisis nuevo antes de agregar el Buró.' }
      case 'ANNEX_IN_PROGRESS':
        return { severity: 'info', summary: 'Búsqueda en curso', detail: 'Ya se está buscando el reporte de Buró; espera un momento y vuelve a abrir la pestaña.' }
      default:
        return { severity: 'warn', summary: 'No se puede agregar el Buró', detail: response.msg }
    }
  }

  protected override onView(): void {
    const sections = this.sections
    this.bureauSection = sections.bureau ?? null
    this.report = this.bureauSection?.status === 'complete' ? (this.bureauSection.data ?? null) : null
    const report = this.report

    const rows = (report?.financialAccounts ?? []).map((account, index) => this.accountRow(account, index))
    this.openAccounts = rows.filter((row) => row.account.isOpen)
    this.closedAccounts = rows.filter((row) => !row.account.isOpen)

    //* Saldos y antigüedad sobre las cuentas ACTIVAS: una pagada no debe nada aunque su historial lo arrastre.
    const open = this.openAccounts.map((row) => row.account)
    this.currentBalance = open.reduce((sum, account) => sum + amount(account.saldoVigente), 0)
    this.overdueBalance = this.openAccounts.reduce((sum, row) => sum + row.overdue, 0)
    this.aging = AGING_BUCKETS.map(({ key, label }) => {
      const total = open.reduce((sum, account) => sum + amount(account[key]), 0)
      return { label, amount: total, share: this.overdueBalance > 0 ? (total / this.overdueBalance) * 100 : 0 }
    })

    //* `BK12_PCT_PROMT` / `NBK12_PCT_PROMT` llegan como fracción (0.97) del calificador de Buró.
    const pct = (value: unknown) => (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Math.round(Number(value) * 100))
    this.onTime = { bank: pct(report?.ratings?.['BK12_PCT_PROMT']), nonBank: pct(report?.ratings?.['NBK12_PCT_PROMT']) }

    const count = (key: string) => {
      const value = report?.inquiries?.[key]
      return value === undefined || value === null || value === '' ? null : Number(value)
    }
    this.inquiries = INQUIRY_WINDOWS.map((months) => ({
      label: `${months} meses`,
      financial: count(`consultaEntidadFinancieraUltimos${months}Meses`),
      commercial: count(`consultaEmpresaComercialUltimos${months}Meses`),
    }))

    //* El historial llega en orden del proveedor; se pinta del mes más reciente al más viejo, 12 meses.
    this.ratingRows = [...(report?.history ?? [])]
      .sort((a, b) => String(b['periodo'] ?? '').localeCompare(String(a['periodo'] ?? '')))
      .slice(0, 12)
      .map((month) => {
        const raw = typeof month['calificacioncartera'] === 'string' ? month['calificacioncartera'] : null
        return { month, rating: portfolioRatingText(raw), raw, items: decodePortfolioRating(raw) }
      })
    this.portfolioRating = decodePortfolioRating(report?.portfolioRating)
    this.scorePercent = scorePosition(report?.score, BUREAU_PYME_SCORE_RANGE)
    this.moratoryAverageRange = lastMonthsRange(sections.moratoryInterest?.data?.monthly, 3)
    this.moratoryBars = this.barsOf(sections.moratoryInterest?.data?.monthly ?? [])
    this.sourcesWithoutRecords = this.withoutRecords(sections)
    if (!this.segments.some((segment) => segment.key === this.segment)) this.segment = this.segments[0]?.key ?? 'financial'
  }

  /** Una barra por mes, a escala del mes más alto; un mes en $0 queda como raya gris de 3 px. */
  private barsOf(monthly: Array<{ d: string; a: number }>): Array<{ month: string; amount: string; height: number; isZero: boolean }> {
    const max = Math.max(0, ...monthly.map((point) => amount(point.a)))
    return [...monthly]
      .sort((a, b) => a.d.localeCompare(b.d))
      .map((point) => {
        const value = amount(point.a)
        const height = value <= 0 || max <= 0 ? MORATORY_BAR_MIN : Math.max(MORATORY_BAR_FLOOR, Math.round((value / max) * 100))
        return { month: this.mxDate.transform(point.d, 'month'), amount: formatPesos(value), height, isZero: value <= 0 }
      })
  }

  /** RUG, Infonavit, moratorios e instituciones que no traen registros bajan a «Sin registros:» con su estado. */
  private withoutRecords(sections: BureauView['sections']): SourceWithoutRecords[] {
    const sources: Array<{ key: keyof BureauView['sections']; label: string }> = [
      { key: 'securedLoans', label: 'RUG · garantías mobiliarias' },
      { key: 'infonavit', label: 'Infonavit' },
      { key: 'moratoryInterest', label: 'Intereses moratorios' },
      { key: 'financialInstitutions', label: 'Instituciones financieras' },
    ]
    return sources.flatMap(({ key, label }) => {
      const state = this.sectionState(sections[key])
      return state ? [{ key, label, state, text: sectionStateText(state, sections[key]?.reason) }] : []
    })
  }

  private accountRow(account: BureauAccount, index: number): BureauAccountRow {
    const mop = worstMop(account.historicoPagos)
    return {
      key: `${index}:${account.account}`,
      account,
      overdue: AGING_BUCKETS.reduce((sum, { key }) => sum + amount(account[key]), 0),
      mop,
      mopLabel: mop ? labelOf(BUREAU_MOP_LABELS, mop) : 'Sin historial',
      mopShort: mop ? labelOf(BUREAU_MOP_SHORT_LABELS, mop) : 'Sin historial',
      mopTone: SEVERITY_TONE[mopSeverity(mop)],
      history: historyTones(account.historicoPagos),
      losses: LOSSES.filter(({ key }) => amount(account[key]) > 0).map(({ key, label }) => ({ label, amount: amount(account[key]) })),
    }
  }

  // ─── ayudas de plantilla ──────────────────────────────────────────────────

  readonly humanize = humanizeCode
  readonly pymeRange = BUREAU_PYME_SCORE_RANGE
  readonly compact = formatPesosCompact
  readonly shortDate = formatShortDate

  /**
   * Pesos sin centavos, como el resto de la pantalla (F-158): las cuentas financieras salían «$688.00» junto a
   * la calificación por mes en «$46,204,000». Un monto que no vino se pinta «—», igual que el resto de los vacíos.
   */
  money(value: number | null | undefined): string {
    return value === null || value === undefined ? '—' : formatPesos(value)
  }

  /**
   * Monto de la calificación por mes o de una cuenta comercial, que Buró da en miles: se pasa a pesos. Sin
   * centavos, porque la cifra original no los trae y pintar «.00» aparentaría una precisión que no hay.
   */
  thousandsAsPesos(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—'
    const n = Number(value)
    return Number.isFinite(n) ? formatPesos(n * BUREAU_THOUSANDS) : String(value)
  }

  /** Opacidad del segmento de un tramo vencido: proporcional a su parte del total, nunca tan tenue que no se vea. */
  agingOpacity(share: number): number {
    return Math.max(0.35, Math.min(1, share / 100))
  }

  /** Lo que el RUG declara de vigencia: la fecha de fin del contrato o la vigencia de su última operación. */
  securedLoanValidity(loan: BureauSectionData['securedLoans']['items'][number]): string {
    if (loan.contractEndDate) return formatShortDate(loan.contractEndDate)
    const validity = [...(loan.operations ?? [])].reverse().find((operation) => operation.validity)?.validity
    return validity ?? '—'
  }

  trackByIndex(index: number): number {
    return index
  }
}
